import { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useIsMounted } from "./useIsMounted";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import { CREATE_NEW_ITEM } from "../../../constants/constants";
import { IVersionedObject } from "../../../planEatApi/PlanEatFacade";
import { useSysadminData } from "../SysadminData";
import { errorMessage, SysadminSection, sysadminPaths } from "../sysadminUtils";

export interface EntityEditorConfig<T> {
    label: string;
    section: SysadminSection;
    empty: T;
    get: (id: string) => Promise<AxiosResponse<IVersionedObject<T>>>;
    getVersion: (id: string, version: string) => Promise<AxiosResponse<IVersionedObject<T>>>;
    post: (content: T) => Promise<AxiosResponse<IVersionedObject<T>>>;
    remove: (id: string) => Promise<AxiosResponse<void>>;
}

export interface EntityMeta {
    version: string;
    versionDateTime: Date;
    creationUser: string;
}

/**
 * Shared state handling for the sysadmin detail pages:
 * loads the latest entity (or a historic version), tracks unsaved changes and saves/deletes/restores.
 */
export function useEntityEditor<T extends { id: string }>(
    config: EntityEditorConfig<T>,
    id: string | undefined,
    version: string | undefined,
    initialForNew?: Partial<T>,
) {
    const history = useHistory();
    const { reload } = useSysadminData();
    const isNew = !id || id === CREATE_NEW_ITEM;
    const isVersion = !isNew && !!version;

    const [entity, setEntity] = useState<T>({ ...config.empty, ...initialForNew });
    const [original, setOriginal] = useState<T>({ ...config.empty, ...initialForNew });
    const [meta, setMeta] = useState<EntityMeta>();
    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string>();
    const [saving, setSaving] = useState(false);
    const mounted = useIsMounted();

    const load = useCallback(async () => {
        if (isNew || !id) return;
        setLoading(true);
        setLoadError(undefined);
        try {
            const response = version ? await config.getVersion(id, version) : await config.get(id);
            if (!mounted.current) return;
            setEntity(response.data.content);
            setOriginal(response.data.content);
            setMeta({
                version: response.data.version,
                versionDateTime: response.data.versionDateTime,
                creationUser: response.data.creationUser,
            });
        } catch (e) {
            if (mounted.current) setLoadError(errorMessage(e, `Could not load ${config.label.toLowerCase()}`));
        } finally {
            if (mounted.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, version, isNew]);

    useEffect(() => {
        load();
    }, [load]);

    function setField<K extends keyof T>(key: K, value: T[K]) {
        setEntity(current => ({ ...current, [key]: value }));
    }

    const dirty = JSON.stringify(entity) !== JSON.stringify(original);

    async function save(): Promise<boolean> {
        setSaving(true);
        try {
            const response = await config.post(entity);
            const saved = response.data.content;
            toast.success(`${config.label} ${isNew ? "created" : isVersion ? "restored" : "saved"}`);
            await reload();
            if (!mounted.current) return true;
            if (isNew || isVersion) {
                history.push(sysadminPaths.detail(config.section, saved.id));
            } else {
                setEntity(saved);
                setOriginal(saved);
                setMeta({ version: response.data.version, versionDateTime: response.data.versionDateTime, creationUser: response.data.creationUser });
            }
            return true;
        } catch (e) {
            toast.error(errorMessage(e, `Could not save ${config.label.toLowerCase()}`));
            return false;
        } finally {
            if (mounted.current) setSaving(false);
        }
    }

    async function remove(redirectTo: string): Promise<boolean> {
        try {
            await config.remove(entity.id);
            toast.success(`${config.label} deleted`);
            await reload();
            history.push(redirectTo);
            return true;
        } catch (e) {
            toast.error(errorMessage(e, `Could not delete ${config.label.toLowerCase()}`));
            return false;
        }
    }

    /** Discards unsaved changes. */
    const reset = () => setEntity(original);

    return { entity, setEntity, setField, meta, isNew, isVersion, loading, loadError, reloadEntity: load, dirty, saving, save, remove, reset };
}
